import { css } from "lit";

/**
 * Mini design system (spec-web-ui §3: "savas CSS: design tokens per CSS
 * custom properties, server_files.html estetika pakelta iki tvarkingos
 * mini sistemos"). Light theme only in v1.
 *
 * MECHANISM (documented decision): `documentTokens` defines every token on
 * `:root` and is adopted ONCE onto the document by the shell entry
 * (adoptedStyleSheets). Custom properties INHERIT through shadow
 * boundaries, so each component's own `static styles` simply uses
 * `var(--m-*)` — no per-component token duplication, and a future theme is
 * one `:root` override away. `controlStyles` is the shared look for
 * buttons/inputs/selects, composed into each component's styles array
 * (constructable sheets are shared by reference — zero duplication at
 * runtime).
 */

export const documentTokens = css`
  :root {
    /* Typography */
    --m-font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    --m-fs-base: 14px;
    --m-fs-small: 12.5px;
    --m-fs-label: 12px;
    --m-fs-title: 17px;

    /* Spacing scale */
    --m-space-1: 4px;
    --m-space-2: 8px;
    --m-space-3: 12px;
    --m-space-4: 16px;
    --m-space-5: 24px;

    /* Color — light theme v1 */
    --m-color-bg: #f4f5f7;
    --m-color-surface: #ffffff;
    --m-color-border: #d9dde3;
    --m-color-border-strong: #b9c0ca;
    --m-color-text: #1f2530;
    --m-color-text-muted: #67707e;
    --m-color-accent: #2f6fb8;
    --m-color-accent-strong: #265d9c;
    --m-color-accent-soft: #eaf2fb;
    --m-color-selected: #d8e7f8;
    --m-color-danger: #b3261e;
    --m-color-danger-soft: #fdecea;
    --m-color-ok: #1a7f37;
    --m-color-ok-soft: #e9f6ec;
    --m-color-focus: #1d4f8c;

    /* Shape */
    --m-radius: 6px;
    --m-radius-small: 4px;
    --m-shadow: 0 1px 2px rgba(31, 37, 48, 0.08);
  }
`;

/** Base document look, adopted alongside the tokens by the shell entry
 * (the shell HTML itself carries no inline styles — CSP default-src 'self'). */
export const documentBase = css`
  body {
    margin: 0;
    background: var(--m-color-bg);
    color: var(--m-color-text);
    font-family: var(--m-font-family);
    font-size: var(--m-fs-base);
  }
`;

/**
 * Shared form-control styling for components. Visible focus is a hard UX
 * requirement (spec quality bar): every interactive element gets the same
 * 2px focus ring via :focus-visible.
 */
export const controlStyles = css`
  button,
  input,
  select {
    font-family: var(--m-font-family);
    font-size: var(--m-fs-base);
    color: var(--m-color-text);
  }

  button {
    padding: 6px 14px;
    border: 1px solid var(--m-color-border-strong);
    border-radius: var(--m-radius-small);
    background: var(--m-color-surface);
    cursor: pointer;
    white-space: nowrap;
  }
  button:hover:not(:disabled) {
    background: var(--m-color-accent-soft);
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
  button.primary {
    background: var(--m-color-accent);
    border-color: var(--m-color-accent-strong);
    color: #fff;
  }
  button.primary:hover:not(:disabled) {
    background: var(--m-color-accent-strong);
  }
  button.linklike {
    border: none;
    background: none;
    color: var(--m-color-accent);
    padding: 2px 4px;
  }
  button.linklike:hover:not(:disabled) {
    background: var(--m-color-accent-soft);
    text-decoration: underline;
  }

  input[type="text"],
  input[type="search"],
  input[type="email"],
  input[type="password"],
  select {
    padding: 6px 8px;
    border: 1px solid var(--m-color-border-strong);
    border-radius: var(--m-radius-small);
    background: var(--m-color-surface);
    width: 100%;
    box-sizing: border-box;
  }

  button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--m-color-focus);
    outline-offset: 1px;
  }

  label {
    display: block;
    font-size: var(--m-fs-label);
    font-weight: 600;
    color: var(--m-color-text-muted);
    margin-bottom: var(--m-space-1);
  }
`;
